import { useMemo, useState } from 'react';
import { Alert, Button, Card, Chip, ChipRow, ChipWrap, Money, SegmentedControl, Select, Sheet } from '../../components';
import { CATEGORIES } from '../../lib/categories';
import { currencyOptions, formatMoney, parseAmount, symbolOf } from '../../lib/money';
import { exactTotal, resolveSplit, SplitError } from '../../lib/split';
import type { ResolvedSplit, SplitMode } from '../../lib/split';
import { resolveRate, saveRate } from '../../data/rates';
import type { KnownRate } from '../../data/rates';
import { createExpense, updateExpense } from '../../data/expenses';
import { fromLocalInputValue, toLocalInputValue } from '../../lib/dates';
import { useSubmit } from '../../hooks/useSubmit';
import type { ExpenseRow, TripMemberRow, TripRow } from '../../data/types';

export interface ExpenseSheetProps {
  open: boolean;
  trip: TripRow;
  members: TripMemberRow[];
  rates: Map<string, KnownRate>;
  myMemberId: string | null;
  /** Present when editing rather than adding. */
  expense: ExpenseRow | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Mounted only while open, so every open starts from clean state rather than
 * from whatever was half-typed last time. All the hooks live in the inner
 * component for that reason.
 */
export function ExpenseSheet(props: ExpenseSheetProps) {
  if (!props.open) return null;
  return <ExpenseForm {...props} />;
}

/** equal and exact are what the UI offers; see the note on `shares` below. */
type UiMode = 'equal' | 'exact';

function ExpenseForm({
  trip,
  members,
  rates,
  myMemberId,
  expense,
  onClose,
  onSaved,
}: ExpenseSheetProps) {
  const isEdit = expense != null;
  const submit = useSubmit();

  const [description, setDescription] = useState(expense?.description ?? '');
  const [amountText, setAmountText] = useState(expense ? String(expense.amount) : '');
  const [currency, setCurrency] = useState(
    expense?.currency ?? trip.local_currency ?? trip.base_currency,
  );
  const [category, setCategory] = useState(expense?.category ?? 'food');
  const [payerId, setPayerId] = useState(
    expense?.payer_member_id ?? myMemberId ?? members[0]?.id ?? '',
  );
  const [spentAt, setSpentAt] = useState(toLocalInputValue(expense?.spent_at ?? new Date()));

  const [included, setIncluded] = useState<string[]>(() =>
    expense ? expense.trip_expense_splits.map((s) => s.member_id) : members.map((m) => m.id),
  );

  // A new expense splits equally — that is the overwhelmingly common case and
  // what the prototype defaults to.
  //
  // On edit, an expense written with mode 'shares' — which this UI cannot
  // create, but the schema and the API contract both allow — opens as an exact
  // split pre-filled with its resolved amounts. Editing it that way preserves
  // the numbers rather than silently re-splitting them equally.
  const [mode, setMode] = useState<UiMode>(
    !expense || expense.split_mode === 'equal' ? 'equal' : 'exact',
  );

  const [exactText, setExactText] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const split of expense?.trip_expense_splits ?? []) {
      seed[split.member_id] = String(split.amount);
    }
    return seed;
  });

  // ---- rate ---------------------------------------------------------------
  // Non-negotiable #5. The rate is a plain, always-visible, editable field, not
  // something behind a "having trouble?" link. On edit it is seeded from the
  // rate frozen on the expense, never re-looked-up — non-negotiable #2.
  const sameCurrency = currency === trip.base_currency;
  const known = useMemo(
    () => (expense ? null : resolveRate(trip.base_currency, currency, rates)),
    [expense, trip.base_currency, currency, rates],
  );

  const [rateText, setRateText] = useState(() => {
    if (expense) return String(expense.rate_at_entry);
    const seeded = resolveRate(trip.base_currency, currency, rates);
    return seeded && seeded.rate !== 1 ? String(seeded.rate) : '';
  });
  const [rateTouched, setRateTouched] = useState(false);

  function changeCurrency(next: string) {
    setCurrency(next);
    if (rateTouched) return; // don't clobber a rate someone deliberately typed
    const seeded = resolveRate(trip.base_currency, next, rates);
    setRateText(seeded && seeded.rate !== 1 ? String(seeded.rate) : '');
  }

  const rate = sameCurrency ? 1 : parseAmount(rateText);

  // ---- derived ------------------------------------------------------------
  const amount = parseAmount(amountText);
  const exactAmounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [id, text] of Object.entries(exactText)) out[id] = parseAmount(text) ?? 0;
    return out;
  }, [exactText]);

  const { preview, problem } = useMemo((): {
    preview: ResolvedSplit | null;
    problem: string | null;
  } => {
    if (amount == null || amount <= 0) return { preview: null, problem: null };
    if (rate == null || rate <= 0) return { preview: null, problem: null };
    if (included.length === 0) {
      return { preview: null, problem: 'Pick at least one person to split this between.' };
    }
    try {
      return {
        preview: resolveSplit({
          amount,
          rate,
          memberIds: included,
          mode: mode as SplitMode,
          exactAmounts,
        }),
        problem: null,
      };
    } catch (err) {
      return { preview: null, problem: err instanceof SplitError ? err.message : String(err) };
    }
  }, [amount, rate, included, mode, exactAmounts]);

  const canSave = preview != null && payerId !== '' && !submit.submitting;

  async function handleSave() {
    if (!preview || rate == null) return;

    const ok = await submit.run(async () => {
      const input = {
        tripId: trip.id,
        payerMemberId: payerId,
        description,
        category,
        currency,
        rate,
        mode: mode as SplitMode,
        spentAt: fromLocalInputValue(spentAt),
        split: preview,
      };

      if (isEdit && expense) await updateExpense(expense.id, input);
      else await createExpense(input);

      // Share a hand-typed rate with the rest of the trip, so nobody else has
      // to find it again. Deliberately after the expense and deliberately
      // swallowed: the rate is already frozen onto the row that matters, and
      // failing to cache it is not a reason to tell someone their dinner
      // didn't save.
      if (rateTouched && !sameCurrency && rate > 0) {
        try {
          await saveRate(trip.base_currency, currency, rate, 'client');
        } catch {
          /* not worth surfacing */
        }
      }
    });

    if (ok) onSaved();
  }

  const currentExactTotal = exactTotal(included, exactAmounts);
  const everyoneIncluded = members.length > 0 && members.every((m) => included.includes(m.id));
  const label = { color: 'var(--text-muted)', fontSize: 'var(--fs-body-md)', margin: '22px 0 10px' };

  return (
    <Sheet
      open
      title={isEdit ? 'Edit expense' : 'New expense'}
      onClose={onClose}
      action={
        <Button size="sm" onClick={() => void handleSave()} disabled={!canSave} loading={submit.submitting}>
          Save
        </Button>
      }
    >
      {submit.error && <Alert tone="error">{submit.error}</Alert>}

      {/* amount + currency */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--sp-2)', marginTop: submit.error ? 'var(--sp-4)' : 0 }}>
        <span className="num" style={{ fontSize: 30, color: 'var(--text-muted)', paddingBottom: 6 }}>
          {symbolOf(currency)}
        </span>
        <input
          inputMode="decimal"
          autoFocus
          value={amountText}
          onChange={(e) => setAmountText(e.target.value)}
          placeholder="0"
          aria-label="Amount"
          className="num"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 46,
            color: 'var(--text)',
            padding: 0,
          }}
        />
        <div style={{ width: 106, paddingBottom: 4 }}>
          <Select
            value={currency}
            onChange={(e) => changeCurrency(e.target.value)}
            aria-label="Currency"
            style={{ minHeight: 40, fontSize: 'var(--fs-body-md)' }}
          >
            {currencyOptions(trip.base_currency, trip.local_currency, expense?.currency).map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {!sameCurrency && (
        <RateField
          base={trip.base_currency}
          quote={currency}
          value={rateText}
          known={known}
          frozen={isEdit && currency === expense?.currency}
          amount={amount}
          rate={rate}
          onChange={(next) => {
            setRateText(next);
            setRateTouched(true);
          }}
        />
      )}

      <div style={{ ...label, marginTop: 22 }}>What was it?</div>
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder={CATEGORIES.find((c) => c.key === category)?.label ?? 'Expense'}
        aria-label="Description"
        style={{
          width: '100%',
          minHeight: 46,
          padding: '0 var(--sp-4)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          fontSize: 'var(--fs-body-lg)',
          outline: 'none',
        }}
      />

      <div style={label}>Category</div>
      <ChipRow>
        {CATEGORIES.map((c) => (
          <Chip key={c.key} selected={category === c.key} onClick={() => setCategory(c.key)}>
            {c.label}
          </Chip>
        ))}
      </ChipRow>

      <div style={label}>When</div>
      <input
        type="datetime-local"
        value={spentAt}
        onChange={(e) => setSpentAt(e.target.value)}
        aria-label="When"
        style={{
          width: '100%',
          minHeight: 46,
          padding: '0 var(--sp-4)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          fontSize: 'var(--fs-body-lg)',
          outline: 'none',
          color: 'var(--text)',
        }}
      />

      {/* Non-negotiable #4: anyone can log an expense on anyone's behalf. */}
      <div style={label}>Paid by</div>
      <ChipWrap>
        {members.map((m) => (
          <Chip key={m.id} selected={payerId === m.id} onClick={() => setPayerId(m.id)}>
            {m.display_name}
          </Chip>
        ))}
      </ChipWrap>

      <div style={label}>Split between</div>
      <ChipWrap>
        {/*
          The form already opens with everyone included, so this is not about
          the default — it is the way back. Deselecting three people to split a
          taxi and then wanting the whole group again otherwise means tapping
          each of them a second time.
        */}
        <Chip
          selected={everyoneIncluded}
          onClick={() => setIncluded(members.map((m) => m.id))}
          title="Split this between the whole group"
        >
          Everyone
        </Chip>
        {members.map((m) => (
          <Chip
            key={m.id}
            selected={included.includes(m.id)}
            onClick={() =>
              setIncluded((current) =>
                current.includes(m.id) ? current.filter((id) => id !== m.id) : [...current, m.id],
              )
            }
          >
            {m.display_name}
          </Chip>
        ))}
      </ChipWrap>

      <div style={{ marginTop: 22 }}>
        <SegmentedControl
          label="How to split"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'equal', label: 'Equally' },
            { value: 'exact', label: 'Exact amounts' },
          ]}
        />
      </div>

      {mode === 'exact' && (
        <Card flush style={{ marginTop: 'var(--sp-4)' }}>
          {members
            .filter((m) => included.includes(m.id))
            .map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-3) var(--sp-5)',
                  borderTop: i > 0 ? '1px solid var(--border)' : 'none',
                }}
              >
                <label htmlFor={`exact-${m.id}`} style={{ fontSize: 'var(--fs-body-lg)' }}>
                  {m.display_name}
                </label>
                <input
                  id={`exact-${m.id}`}
                  inputMode="decimal"
                  value={exactText[m.id] ?? ''}
                  placeholder="0.00"
                  onChange={(e) => setExactText({ ...exactText, [m.id]: e.target.value })}
                  className="num"
                  style={{
                    width: 100,
                    textAlign: 'right',
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontSize: 'var(--fs-body-lg)',
                    color: 'var(--text)',
                  }}
                />
              </div>
            ))}
        </Card>
      )}

      <SplitPreview
        preview={preview}
        problem={problem}
        mode={mode}
        currency={currency}
        baseCurrency={trip.base_currency}
        included={included}
        exactSoFar={currentExactTotal}
        amount={amount}
      />
    </Sheet>
  );
}

/**
 * The exchange rate field.
 *
 * Presented as an ordinary input with a value already in it, rather than as a
 * fallback that appears when something fails, because on this trip the fetch
 * failing is the expected case, not the exceptional one. Where the number came
 * from is stated underneath, since a seeded guess and a rate somebody actually
 * looked up deserve different amounts of trust — and once saved, whichever one
 * it was gets frozen onto the expense forever.
 */
function RateField({
  base,
  quote,
  value,
  known,
  frozen,
  amount,
  rate,
  onChange,
}: {
  base: string;
  quote: string;
  value: string;
  known: KnownRate | null;
  /** Editing an expense in its original currency: this rate is the stored one. */
  frozen: boolean;
  amount: number | null;
  rate: number | null;
  onChange: (next: string) => void;
}) {
  const converted = amount != null && rate != null && rate > 0 ? amount * rate : null;

  let provenance: { text: string; tone: 'muted' | 'warning' };
  if (frozen) {
    provenance = {
      text: 'The rate this expense was saved with. Changing it re-converts this expense, and only this one.',
      tone: 'muted',
    };
  } else if (known?.source === 'seed') {
    provenance = {
      text: `Estimated rate — nobody has set one for ${quote} yet. Check it before saving.`,
      tone: 'warning',
    };
  } else if (known?.source === 'client') {
    provenance = { text: `Set by hand${known.asOf ? ` on ${known.asOf}` : ''}.`, tone: 'muted' };
  } else if (known?.source === 'provider') {
    provenance = { text: `Fetched${known.asOf ? ` ${known.asOf}` : ''}.`, tone: 'muted' };
  } else {
    provenance = { text: `No rate stored for ${quote}. Type one in.`, tone: 'warning' };
  }

  return (
    <div style={{ marginTop: 'var(--sp-4)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          padding: 'var(--sp-3) var(--sp-4)',
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
        }}
      >
        <label htmlFor="rate" style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-secondary)' }}>
          1 {quote} =
        </label>
        <input
          id="rate"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          className="num"
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            fontSize: 'var(--fs-body-lg)',
            color: 'var(--text)',
          }}
        />
        <span style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-secondary)' }}>{base}</span>
      </div>

      <div
        style={{
          marginTop: 'var(--sp-2)',
          fontSize: 'var(--fs-caption)',
          color: provenance.tone === 'warning' ? 'var(--warning)' : 'var(--text-muted)',
        }}
      >
        {provenance.text}
      </div>

      {converted != null && (
        <div style={{ marginTop: 'var(--sp-1)', fontSize: 'var(--fs-body-md)', color: 'var(--text-secondary)' }}>
          ≈ {formatMoney(converted, base)} {base}
        </div>
      )}
    </div>
  );
}

/** What each person ends up owing — the same numbers that will be written. */
function SplitPreview({
  preview,
  problem,
  mode,
  currency,
  baseCurrency,
  included,
  exactSoFar,
  amount,
}: {
  preview: ResolvedSplit | null;
  problem: string | null;
  mode: UiMode;
  currency: string;
  baseCurrency: string;
  included: string[];
  exactSoFar: number;
  amount: number | null;
}) {
  if (problem) {
    return (
      <div style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)', color: 'var(--error)' }}>
        {problem}
      </div>
    );
  }

  if (!preview) {
    if (mode === 'exact' && included.length > 0 && amount != null) {
      return (
        <div style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>
          {formatMoney(exactSoFar, currency)} of {formatMoney(amount, currency)} assigned.
        </div>
      );
    }
    return null;
  }

  // Equal splits are one line; an uneven remainder gets a range so nobody is
  // surprised by the odd cent on their own row.
  const amounts = preview.shares.map((s) => s.amount);
  const low = Math.min(...amounts);
  const high = Math.max(...amounts);

  return (
    <div style={{ marginTop: 'var(--sp-4)', fontSize: 'var(--fs-body-md)', color: 'var(--text-secondary)' }}>
      {mode === 'equal' && (
        <div>
          {low === high
            ? `${formatMoney(low, currency)} each`
            : `${formatMoney(low, currency)}–${formatMoney(high, currency)} each`}
          {preview.shares.length > 1 && ` · ${preview.shares.length} people`}
        </div>
      )}
      {currency !== baseCurrency && (
        <div style={{ marginTop: 'var(--sp-1)', color: 'var(--text-muted)', fontSize: 'var(--fs-body-sm)' }}>
          Recorded as <Money amount={preview.baseAmount} currency={baseCurrency} /> {baseCurrency} at
          today’s rate, and it stays at that rate.
        </div>
      )}
    </div>
  );
}
